import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';

import { TenantProcedures } from './TenantProcedures';
import { TenantProcedureInstances } from './TenantProcedureInstances';

/**
 * Procedures — the tenant-admin procedure catalogue and the procedure
 * instances, consolidated into one tabbed page. Each panel is the original
 * page component in `embedded` mode (its own title suppressed; page-level
 * actions kept). The active tab lives in the URL (`?tab=procedures|instances`)
 * so the old /tenant/procedure-instances route deep-links to the right tab.
 */
const TABS = [
  {
    key: 'procedures',
    label: 'Procedures',
    render: (slot: HTMLElement | null) => (
      <TenantProcedures embedded actionsSlot={slot} />
    ),
  },
  {
    key: 'instances',
    label: 'Instances',
    render: (slot: HTMLElement | null) => (
      <TenantProcedureInstances embedded actionsSlot={slot} />
    ),
  },
] as const;

export function TenantProceduresPage() {
  const [params, setParams] = useSearchParams();
  const active = Math.max(
    0,
    TABS.findIndex((t) => t.key === params.get('tab')),
  );
  // The active tab's page-level actions (New procedure / Refresh / Status
  // filter) portal into this slot so they sit on the tab bar's line rather
  // than a separate row below.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Procedures</Typography>
        <Typography variant="body2" color="text.secondary">
          Your procedure catalogue and the instances your teams perform.
        </Typography>
      </Stack>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={active}
          onChange={(_e, v: number) =>
            setParams({ tab: TABS[v].key }, { replace: true })
          }
          aria-label="Procedures sections"
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        {/* Slot the active tab's actions portal into. */}
        <Box
          ref={setActionsSlot}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}
        />
      </Box>

      <Box>{TABS[active].render(actionsSlot)}</Box>
    </Stack>
  );
}
